import React from 'react';
import { createCodebookCache } from '../utils/codebookCache';

interface UnitOption {
  _id: string;
  code: string;
  name?: string;
  nameEn?: string;
}

interface UnitMeasurmentSelectProps {
  value: string | null | undefined;
  onChange: (id: string | null) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

// ─── Unit measurment cache (via shared codebook-cache factory) ───────────────
// SWR + subscriber broadcast + auto-invalidate on writes all come from the factory —
// see utils/codebookCache.ts. This used to hand-roll `_cache`/`_inflight`, which meant no
// broadcast: under keep-alive open-tabs a screen that never remounts kept a stale list
// until F5, and editing the codebook elsewhere did not refresh anything.
//
// The read path is the lean `/active` sub-route, but writes go to the collection root, so
// `invalidateOn` is what keeps auto-invalidation working here.

const unitMeasurmentCache = createCodebookCache<UnitOption>({
  endpoint: '/api/unit-measurments/active',
  invalidateOn: '/api/unit-measurments',
  select: (data) => (Array.isArray(data) ? data : data?.unitMeasurments || []),
});

/** Call after any successful create/update/delete so next mount fetches fresh data. */
export const invalidateUnitMeasurmentCache = unitMeasurmentCache.invalidate;
/** Hook: active unit measurment options (stale-while-revalidate, live-updating). */
export const useUnitMeasurmentOptions = (): UnitOption[] => unitMeasurmentCache.useOptions();

/**
 * Resolve a unit measurment _id to its code from the already-loaded cache (sync, no fetch).
 * Returns '' when the codebook has not been loaded in this session yet — callers use it as a
 * fallback behind a value they already hold.
 */
export const getCachedUnitMeasurmentCode = (id: string | null | undefined): string => {
  if (!id) return '';
  return unitMeasurmentCache.peek()?.find((u) => u._id === id)?.code || '';
};

const UnitMeasurmentSelect: React.FC<UnitMeasurmentSelectProps> = ({
  value,
  onChange,
  disabled = false,
  placeholder = '—',
  className = '',
}) => {
  const options = useUnitMeasurmentOptions();

  return (
    <select
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value || null)}
      disabled={disabled}
      className={`h-10 px-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 ${
        disabled ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'
      } ${className}`}
    >
      <option value="">{placeholder}</option>
      {options.map((u) => (
        <option key={u._id} value={u._id}>
          {u.code}
        </option>
      ))}
    </select>
  );
};

export default UnitMeasurmentSelect;
